/**
 * Firestore service for user folders
 */
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
  type WriteBatch,
} from 'firebase/firestore';

import { auth, firestore } from '@/services/firebase';
import type { Folder } from '@/types/card';
import type { CreateFolder } from '@/types/schemas';

function getUserFoldersRef(userId: string) {
  return collection(firestore, 'users', userId, 'folders');
}

function getCurrentUserId(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
}

/**
 * Get all folders for current user
 */
export async function getFolders(): Promise<Folder[]> {
  const userId = getCurrentUserId();
  const foldersRef = getUserFoldersRef(userId);
  const snapshot = await getDocs(foldersRef);

  const folders = snapshot.docs.map(
    (docSnap) =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
      }) as Folder,
  );

  // Sort in memory: by sort_order, then by name
  return folders.sort((a, b) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.name.localeCompare(b.name);
  });
}

/**
 * Get a folder by ID
 */
export async function getFolderById(folderId: string): Promise<Folder | null> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'folders', folderId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() } as Folder;
}

/**
 * Get folder by name (for import matching)
 */
export async function getFolderByName(name: string): Promise<Folder | null> {
  const userId = getCurrentUserId();
  const foldersRef = getUserFoldersRef(userId);
  const q = query(foldersRef, where('name', '==', name));
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() } as Folder;
}

/**
 * Create a new folder
 */
export async function createFolder(data: CreateFolder): Promise<string> {
  const userId = getCurrentUserId();
  const foldersRef = getUserFoldersRef(userId);

  // Get max sort order
  const folders = await getFolders();
  const maxSortOrder = folders.reduce((max, f) => Math.max(max, f.sort_order), 0);

  const docData = {
    name: data.name,
    color: data.color ?? null,
    icon: data.icon ?? null,
    parent_id: data.parent_id ?? null,
    sort_order: maxSortOrder + 1,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  const docRef = await addDoc(foldersRef, docData);
  return docRef.id;
}

/**
 * Get or create folder by name (for import)
 */
export async function getOrCreateFolder(name: string): Promise<string> {
  const existing = await getFolderByName(name);
  if (existing) {
    return existing.id;
  }
  return createFolder({ name });
}

/**
 * Get or create folder by name under a specific parent
 * Used for import with folder structure preservation
 *
 * @param name - Folder name
 * @param parentId - Parent folder ID (null for root)
 * @returns Folder ID
 */
export async function getOrCreateFolderUnderParent(name: string, parentId: string | null): Promise<string> {
  const userId = getCurrentUserId();
  const foldersRef = getUserFoldersRef(userId);

  // Query for existing folder with this name under the specified parent
  const q =
    parentId === null
      ? query(foldersRef, where('name', '==', name), where('parent_id', '==', null))
      : query(foldersRef, where('name', '==', name), where('parent_id', '==', parentId));

  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    return snapshot.docs[0].id;
  }

  // Create new folder under parent
  return createFolder({ name, parent_id: parentId });
}

/**
 * Update a folder
 */
export async function updateFolder(folderId: string, data: Partial<Omit<Folder, 'id' | 'created_at' | 'updated_at'>>): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'folders', folderId);

  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp(),
  });
}

/**
 * Delete a folder (moves cards to no folder)
 */
export async function deleteFolder(folderId: string): Promise<void> {
  const userId = getCurrentUserId();

  // Move all cards in this folder to "no folder"
  const collectionRef = collection(firestore, 'users', userId, 'collection');
  const cardsQuery = query(collectionRef, where('folder_id', '==', folderId));
  const cardsSnapshot = await getDocs(cardsQuery);

  const batch = writeBatch(firestore);

  // Update cards to remove folder reference
  cardsSnapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.ref, {
      folder_id: null,
      updated_at: serverTimestamp(),
    });
  });

  // Delete the folder
  const folderRef = doc(firestore, 'users', userId, 'folders', folderId);
  batch.delete(folderRef);

  await batch.commit();
}

/**
 * Reorder folders
 */
export async function reorderFolders(folderIds: string[]): Promise<void> {
  const userId = getCurrentUserId();
  const batch = writeBatch(firestore);

  folderIds.forEach((folderId, index) => {
    const docRef = doc(firestore, 'users', userId, 'folders', folderId);
    batch.update(docRef, {
      sort_order: index,
      updated_at: serverTimestamp(),
    });
  });

  await batch.commit();
}

/**
 * Bump a folder's updated_at timestamp
 */
export async function bumpFolderUpdatedAt(folderId: string): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'folders', folderId);
  await updateDoc(docRef, { updated_at: serverTimestamp() });
}

/**
 * Add a folder updated_at bump to an existing batch
 */
export function bumpFolderUpdatedAtInBatch(batch: WriteBatch, ref: DocumentReference): void {
  batch.update(ref, { updated_at: serverTimestamp() });
}

/**
 * Get folder path string by traversing parent chain
 * @param folderId - The folder ID to get path for (null returns null)
 * @param folders - Array of all folders to search through
 * @returns Full path string e.g. "90s/Lands", or null if no folder
 */
export function getFolderPath(folderId: string | null, folders: Folder[]): string | null {
  if (!folderId) return null;

  const folderMap = new Map(folders.map((f) => [f.id, f]));
  const pathParts: string[] = [];

  let currentId: string | null = folderId;
  const visited = new Set<string>();

  // Traverse up the parent chain
  while (currentId) {
    // Prevent infinite loops from circular references
    if (visited.has(currentId)) break;
    visited.add(currentId);

    const folder = folderMap.get(currentId);
    if (!folder) break;

    pathParts.unshift(folder.name);
    currentId = folder.parent_id;
  }

  return pathParts.length > 0 ? pathParts.join('/') : null;
}

/**
 * Delete ALL folders for user
 * Used for testing/development - clears all folders
 * Note: Collection cards keep their folder_id references but become orphaned
 */
export async function clearAllFolders(onProgress?: (cleared: number, total: number) => void): Promise<number> {
  const userId = getCurrentUserId();
  const foldersRef = getUserFoldersRef(userId);
  const snapshot = await getDocs(foldersRef);

  if (snapshot.empty) return 0;

  const batchSize = 500;
  const docs = snapshot.docs;
  const total = docs.length;
  let deletedCount = 0;

  onProgress?.(0, total);

  for (let i = 0; i < docs.length; i += batchSize) {
    const batch = writeBatch(firestore);
    const chunk = docs.slice(i, i + batchSize);

    for (const docSnap of chunk) {
      batch.delete(docSnap.ref);
    }

    await batch.commit();
    deletedCount += chunk.length;
    onProgress?.(deletedCount, total);
  }

  return deletedCount;
}
